import React, { useState } from 'react';

const SessionPlannerInsights = ({ insights }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedMethod, setExpandedMethod] = useState(null);

  const {
    successful_methods = [],
    current_challenges = [],
    recommended_next_steps = []
  } = insights;

  // Filter successful methods based on active filter
  const getFilteredMethods = () => {
    if (activeFilter === 'all') return successful_methods;

    const keywords = {
      games: ['game', 'play', 'interactive', 'activity'],
      methods: ['step-by-step', 'guided', 'instruction', 'teaching', 'explanation'],
      engagement: ['conversation', 'rapport', 'humor', 'encouragement', 'motivation']
    };

    return successful_methods.filter(method => {
      const methodText = method.method.toLowerCase();
      return keywords[activeFilter]?.some(keyword => methodText.includes(keyword));
    });
  };

  const filteredMethods = getFilteredMethods();

  return (
    <div className="sp-insights">
      {/* What Worked Well Section */}
      <div className="sp-card">
        <div className="sp-card__header">
          <h2 className="sp-card__title">What's Worked Best</h2>
          <span className="sp-card__meta">from {insights.total_sessions} sessions</span>
        </div>

        {/* Filter Buttons */}
        <div className="sp-filters">
          <FilterButton
            active={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
            label="All"
          />
          <FilterButton
            active={activeFilter === 'games'}
            onClick={() => setActiveFilter('games')}
            label="Games"
          />
          <FilterButton
            active={activeFilter === 'methods'}
            onClick={() => setActiveFilter('methods')}
            label="Methods"
          />
          <FilterButton
            active={activeFilter === 'engagement'}
            onClick={() => setActiveFilter('engagement')}
            label="Engagement"
          />
        </div>

        {/* Methods List */}
        <div className="sp-card__body">
          {filteredMethods.length > 0 ? (
            <div className="sp-method-list">
              {filteredMethods.map((method, idx) => (
                <MethodCard
                  key={idx}
                  method={method}
                  expanded={expandedMethod === idx}
                  onToggle={() => setExpandedMethod(expandedMethod === idx ? null : idx)}
                />
              ))}
            </div>
          ) : (
            <p className="sp-text sp-text--muted" style={{ textAlign: 'center' }}>
              No methods found for this filter
            </p>
          )}
        </div>
      </div>

      {/* Current Challenges Section */}
      <div className="sp-card" style={{ marginTop: '1.5rem' }}>
        <div className="sp-card__header">
          <h2 className="sp-card__title">Current Challenges</h2>
          <span className="sp-card__meta">from last 3 sessions</span>
        </div>

        <div className="sp-card__body">
          {current_challenges.length > 0 ? (
            <ul className="sp-list">
              {current_challenges.map((challenge, idx) => (
                <li key={idx} className="sp-list-item sp-list-item--warning">
                  {challenge}
                </li>
              ))}
            </ul>
          ) : (
            <p className="sp-text sp-text--muted" style={{ textAlign: 'center' }}>
              No current challenges identified
            </p>
          )}
        </div>
      </div>

      {/* Recommended Next Steps Section */}
      <div className="sp-card" style={{ marginTop: '1.5rem' }}>
        <div className="sp-card__header">
          <h2 className="sp-card__title">Recommended Focus</h2>
        </div>

        <div className="sp-card__body">
          {recommended_next_steps.length > 0 ? (
            <ul className="sp-list">
              {recommended_next_steps.map((step, idx) => (
                <li key={idx} className="sp-list-item sp-list-item--success">
                  {step}
                </li>
              ))}
            </ul>
          ) : (
            <p className="sp-text sp-text--muted" style={{ textAlign: 'center' }}>
              No specific recommendations available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Filter Button Component
const FilterButton = ({ active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`sp-filter-btn ${active ? 'sp-filter-btn--active' : ''}`}
  >
    {label}
  </button>
);

// Method Card Component
const MethodCard = ({ method, expanded, onToggle }) => (
  <div
    className={`sp-method-item ${expanded ? 'sp-method-item--expanded' : ''}`}
    onClick={onToggle}
  >
    <div className="sp-method-item__header">
      <div>
        <h4 className="sp-method-item__title">{method.method}</h4>
        <p className="sp-method-item__meta">
          Used in {method.frequency} session{method.frequency !== 1 ? 's' : ''}
        </p>
      </div>
      <span className="sp-method-item__chevron">{expanded ? '▾' : '▸'}</span>
    </div>

    {expanded && method.examples && method.examples.length > 0 && (
      <div className="sp-method-item__content">
        <span className="sp-label">Examples from sessions</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
          {method.examples.map((example, idx) => (
            <div key={idx} className="sp-activity">
              <span className="sp-label" style={{ color: 'var(--lrbrain-accent)' }}>
                {example.session_id}
              </span>
              <p className="sp-text sp-text--small" style={{ margin: '0.25rem 0 0 0' }}>
                {example.full_text}
              </p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default SessionPlannerInsights;
